import moment from 'moment-msdate';
import _compact from 'lodash/compact';

import format from '../../../../utils/format';
/*Restrictions are based on plans and their holdings, the screener can display info from multiple plans at once
The below logic is to check for and retrieve restrictions for a single fund */

// Each text string is a function to accept inputs for dates, fund names, etc.
export const opening = activeDate =>
  `The investment will be available to receive new contributions (or transferred accumulations) as of ${activeDate}.`;
export const closing = inactiveDate =>
  `The investment will not be available to receive new contributions (or transferred accumulations) as of ${inactiveDate}, and existing accumulations will be "mapped out" to a new investment.`;
export const restricted = () =>
  `The investment will not be available to receive new contributions (or transferred accumulations), but existing accumulations in the investment may remain invested in it.`;
export const cpRestricted = investmentLongName =>
  `The Plan Fiduciary and/or the Plan Advisor have determined that the ${investmentLongName} is appropriate for a Model when combined with other underlying investments, but is not appropriate as a stand-alone investment option for a Participant that is not participating in the TIAA RetirePlus Pro Model Service. Participants who elect to unsubscribe from the Service while holding the ${investmentLongName} will be prohibited from allocating future contributions to the investment option. With regard to existing accumulations, depending on the type of contract, Participants may be required to transfer completely out of the investment option(s) deemed inappropriate as a stand-alone investment option.`;
export const cpOpeningAndRestricted = (activeDate, investmentLongName) => [
  opening(activeDate),
  `The Plan Fiduciary and/or the Plan Advisor have determined that the ${investmentLongName} is appropriate for a Model when combined with other underlying investments, but is not appropriate as a stand-alone investment option for a Participant that is not participating in the TIAA RetirePlus Pro Model Service. Participants who elect to unsubscribe from the Service while holding the ${investmentLongName} will be prohibited from allocating future contributions to the investment option. With regard to existing accumulations, depending on the type of contract, Participants may be required to transfer completely out of the investment option(s) deemed inappropriate as a stand-alone investment option.`
];
export const cpClosingAndRestricted = (inactiveDate, investmentLongName) => [
  closing(inactiveDate),
  `The Plan Fiduciary and/or the Plan Advisor have determined that the ${investmentLongName} is appropriate for a Model when combined with other underlying investments, but is not appropriate as a stand-alone investment option for a Participant that is not participating in the TIAA RetirePlus Pro Model Service. Participants who elect to unsubscribe from the Service while holding the ${investmentLongName} will be prohibited from allocating future contributions to the investment option. With regard to existing accumulations, depending on the type of contract, Participants may be required to transfer completely out of the investment option(s) deemed inappropriate as a stand-alone investment option.`
];
export const cpRestrictedAndRestricted = investmentLongName => [
  restricted(),
  cpRestricted(investmentLongName)
];

/*Find the individual holdings that match xid */
const getHoldingsMatchList = ({ xid, planHoldingsList }) => {
  const holdingMatchList = [];
  planHoldingsList?.forEach(planHoldings => {
    if (planHoldings.holdings) {
      holdingMatchList.push(planHoldings.holdings.find(singleHolding => singleHolding.xid === xid));
    }
  });
  return holdingMatchList;
};

const getBoolFromString = val => {
  let bool = false;
  if (val === 'Y') {
    bool = true;
  }
  return bool;
};


const checkForRestriction = holding => {
  /*Individual Restrictions come from info in a single plan holding. 'contribution' and 'transfer' are keys.
  When they return with 'Y', the fund is not restricted, if either column is empty or any other text, the fund is restricted.*/
  const today = moment();
  const allowContributions = getBoolFromString(holding.contribution);
  const allowTransfers = getBoolFromString(holding.transfer);
  const isRestricted = !allowContributions || !allowTransfers;

  const isCPHoldingRestricted = holding.isCPHoldingRestricted === 1;
  const planConsent = holding.planConsent === 1;
  const activeDate = holding.activeDate ? moment(holding.activeDate) : undefined;
  const inactiveDate = holding.inactiveDate ? moment(holding.inactiveDate) : undefined;

  if (planConsent && activeDate > today && !isCPHoldingRestricted) {
    return opening(format.date(activeDate));
  }

  if (planConsent && activeDate > today && isCPHoldingRestricted) {
    return cpOpeningAndRestricted(format.date(activeDate), holding.investmentLongName);
  }

  if (planConsent && inactiveDate > today && !isRestricted && !isCPHoldingRestricted) {
    return closing(format.date(inactiveDate));
  }

  if (planConsent && inactiveDate > today && isCPHoldingRestricted) {
    return cpClosingAndRestricted(format.date(inactiveDate), holding.investmentLongName);
  }

  if (isCPHoldingRestricted && isRestricted) {
    return cpRestrictedAndRestricted(holding.investmentLongName);
  }

  if (isCPHoldingRestricted) {
    return cpRestricted(holding.investmentLongName);
  }

  if (isRestricted) {
    return restricted();
  }

  return undefined;
};

const getHoldingRestriction = ({ holding, multiplePlans }) => {
  //If there are multiplePlans showing in the screener, we need to display the plan legal name next to the restriction
  const planLegalName = multiplePlans ? `<strong>${holding.planLegalName}:</strong> ` : '';
  //passing through logic to check for restriction
  let restrictionText = checkForRestriction(holding);

  if (multiplePlans && restrictionText !== undefined) {
    restrictionText = `${planLegalName}${restrictionText}`;
  }
  return restrictionText;
};

const getRestrictionsText = (xid, planHoldingsList, isPlanInContext) => {
  if (!isPlanInContext) return undefined;

  const holdingMatchList = _compact(getHoldingsMatchList({ xid, planHoldingsList }));

  if (!holdingMatchList) return undefined;
  const multiplePlans = holdingMatchList.length > 1;
  //A fund can have multiple restrictions if there are multiple plans, otherwise it should be just one.
  const restrictionDisclosures = holdingMatchList.map(holding => getHoldingRestriction({ holding, multiplePlans }));
  return _compact(restrictionDisclosures);

};
export default getRestrictionsText;